import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';

interface NavItem {
  label: string;
  path: string;
  icon: string;
}

interface NavSection {
  title: string;
  icon: string;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Vue globale',
    icon: '📊',
    items: [
      { label: 'Dashboard',     path: '/admin/dashboard',     icon: '📊' },
      { label: 'Organisations', path: '/admin/organizations', icon: '🏢' },
      { label: 'Utilisateurs',  path: '/admin/users',         icon: '👥' },
      { label: 'Abonnements',   path: '/admin/subscriptions', icon: '💳' },
    ],
  },
  {
    title: 'Catalogue',
    icon: '🎯',
    items: [
      { label: 'Plans',       path: '/admin/plans',       icon: '🎯' },
      { label: 'Codes Promo', path: '/admin/promo-codes', icon: '🏷️' },
    ],
  },
  {
    title: 'Infrastructure',
    icon: '🖥️',
    items: [
      { label: 'Infra ADO',       path: '/admin/infrastructure',        icon: '🖥️' },
      { label: 'Onboarding Ent.', path: '/admin/enterprise-onboarding', icon: '🚀' },
      { label: 'Lanes Ent.',      path: '/admin/enterprise-lanes',      icon: '⚡' },
      { label: 'Lanes Bus.',      path: '/admin/business-lanes',        icon: '⚡' },
    ],
  },
  {
    title: 'Configuration',
    icon: '⚙️',
    items: [
      { label: 'Config SMTP',       path: '/admin/smtp-config',     icon: '📧' },
      { label: 'Config Stripe',     path: '/admin/stripe-config',   icon: '⚡' },
      { label: 'Azure Storage',     path: '/admin/azure-storage',   icon: '🗄️' },
      { label: 'Config Plateforme', path: '/admin/platform-config', icon: '🌍' },
      { label: 'Configuration IA',  path: '/admin/ai-config',       icon: '🤖' },
    ],
  },
  {
    title: 'Sécurité',
    icon: '🔍',
    items: [
      { label: 'Audit Logs', path: '/admin/audit-logs', icon: '🔍' },
    ],
  },
];

const AdminLayout: React.FC = () => {
  const { adminUser, adminLogout } = useAdminAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [openSection, setOpenSection] = useState<string | null>('Vue globale');

  const toggleSection = (title: string) => {
    setOpenSection(prev => prev === title ? null : title);
  };

  const handleLogout = () => {
    adminLogout();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="h-screen bg-[#050d17] flex overflow-hidden">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'w-60' : 'w-14'
        } flex-shrink-0 bg-[#0b1c2c] border-r border-slate-700/40 flex flex-col transition-all duration-200 h-full`}
      >
        {/* Logo */}
        <div className="h-14 flex-shrink-0 flex items-center px-3 border-b border-slate-700/40 gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-red-700 flex items-center justify-center text-white font-bold text-sm">
            G
          </div>
          {sidebarOpen && (
            <div>
              <p className="text-white font-semibold text-sm leading-tight">GenIaC</p>
              <p className="text-red-400 text-xs font-medium">Super Admin</p>
            </div>
          )}
        </div>

        {/* Nav — mode étendu : accordion / mode réduit : icônes section avec flyout */}
        <nav className={`flex-1 py-2 ${sidebarOpen ? 'px-2 overflow-y-auto space-y-0.5' : 'px-1 overflow-visible space-y-1'}`}>
          {NAV_SECTIONS.map((section) => {
            const isOpen = openSection === section.title;

            /* ── MODE RÉDUIT ── */
            if (!sidebarOpen) {
              return (
                <div key={section.title} className="relative group">
                  {/* Icône section (plus grande) */}
                  <button
                    className="w-full flex items-center justify-center p-2.5 rounded-lg text-slate-400
                               hover:bg-slate-700/40 hover:text-white transition-colors"
                  >
                    <span className="text-xl leading-none">{section.icon}</span>
                  </button>

                  {/* Flyout au hover */}
                  <div className="pointer-events-none group-hover:pointer-events-auto
                                  absolute left-full top-0 ml-1 z-50
                                  opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    <div className="bg-[#0b1c2c] border border-slate-700/60 rounded-lg shadow-xl min-w-[160px] py-1.5">
                      {/* Titre section */}
                      <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                        {section.title}
                      </p>
                      {/* Items */}
                      {section.items.map((item) => (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          className={({ isActive }) =>
                            `flex items-center gap-2.5 px-3 py-2 text-sm font-medium transition-colors ${
                              isActive
                                ? 'text-red-300 bg-red-900/20'
                                : 'text-slate-300 hover:text-white hover:bg-slate-700/40'
                            }`
                          }
                        >
                          <span className="text-base">{item.icon}</span>
                          <span>{item.label}</span>
                        </NavLink>
                      ))}
                    </div>
                    {/* Flèche */}
                    <div className="absolute right-full top-4 border-6 border-transparent border-r-slate-700/60
                                    translate-x-px" style={{ borderWidth: 6 }} />
                  </div>
                </div>
              );
            }

            /* ── MODE ÉTENDU ── */
            return (
              <div key={section.title}>
                <button
                  onClick={() => toggleSection(section.title)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold
                             uppercase tracking-wider text-slate-500 hover:text-white hover:bg-slate-700/20
                             transition-colors"
                >
                  <span className="flex-1 text-left">{section.title}</span>
                  <span className={`text-slate-600 text-sm transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}>
                    ›
                  </span>
                </button>

                {isOpen && (
                  <div className="ml-3 mt-0.5 mb-1 space-y-0.5 border-l border-slate-700/40 pl-2">
                    {section.items.map((item) => (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                            isActive
                              ? 'bg-red-900/30 text-red-300 border border-red-700/30'
                              : 'text-slate-400 hover:bg-slate-700/30 hover:text-white'
                          }`
                        }
                      >
                        <span className="text-base flex-shrink-0">{item.icon}</span>
                        <span>{item.label}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Toggle */}
        <div className="flex-shrink-0 p-2 border-t border-slate-700/40">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-lg
                       text-slate-400 hover:text-white hover:bg-slate-700/30 transition-colors text-xs font-medium"
          >
            <span>{sidebarOpen ? '◀' : '▶'}</span>
            {sidebarOpen && <span>Réduire</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
        <header className="h-14 flex-shrink-0 bg-[#0b1c2c] border-b border-slate-700/40 flex items-center justify-between px-6">
          <h1 className="text-white font-semibold text-base">Panneau d'administration</h1>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-red-900/50 border border-red-700/40 flex items-center justify-center text-red-300 text-sm font-bold">
                {adminUser?.email?.[0]?.toUpperCase() || 'A'}
              </div>
              <div className="hidden sm:block">
                <p className="text-white text-sm font-medium leading-tight">
                  {adminUser?.full_name || adminUser?.email}
                </p>
                <p className="text-red-400 text-xs">Super Admin</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="px-3 py-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-red-900/30
                         border border-transparent hover:border-red-700/30 text-sm transition-colors"
            >
              Déconnexion
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-auto bg-gray-100 text-gray-900">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
